/**
 * TLDR; To replicate the issue which caused me to add this file:
 * 1. Add a v4 list block
 * 2. Add a form to it.
 * 3. Add a phone input to it.
 * 4. When rendering, the phone input country selector dropdown will be squished.
 * This is because `phone-input.tsx` depends on the order of stylesheets in the head.
 *
 * Explanation:
 * Some v4 react block use emotion for styling.
 * Emotion inserts CSS into the head of the document dynamically.
 *
 * We need some mechanism to ensure that the CSS inserted by emotion
 * comes *after* the block's other stylesheets.
 *
 * headStylesSentinel helps ensure this by inserting a sentinel style node.
 * React18Block.tsx looks for this sentinel and inserts the block's styles
 * just before this position ensuring that emotion's styles are always more
 * specific.
 *
 * {@link SoftrPageRenderer.firstRender} ensures that the sentinel is
 * inserted into the head before any blocks are rendered, ensuring that
 * the block style specificity is maintained.
 *
 * @example
 * firstRender() {
 *   insertHeadStylesSentinel(contentRoot);
 *   // <head>
 *   //   <style data-spr-role="head-styles-sentinel"></style>
 *   // </head>
 *
 *   ReactDOM.render(contentRoot, <Page />)
 *   // Inside `React18Block.tsx`
 *   findHeadStylesSentinel().before(blockStyles);
 *
 *   // <head>
 *   //   <style>
 *   //     // block's CSS
 *   //   </style>
 *   //   <style data-spr-role="head-styles-sentinel"></style>
 *   //   ...
 *   //   Styles dynamically inserted by emotion
 *   //   <style data-emotion="css-global" data-s>...</style>
 *   // </head>
 * }
 *
 */

export function insertHeadStylesSentinel() {
    const headStylesSentinel = document.createElement("style");
    headStylesSentinel.setAttribute("data-spr-role", "head-styles-sentinel");
    document.head.appendChild(headStylesSentinel);
}

export function getHeadStylesSentinel(): Element {
    const selector = "style[data-spr-role='head-styles-sentinel']";
    const sentinel = document.querySelector(selector);
    if (!sentinel) {
        throw new Error(
            `Could not find the element: ${selector}; Make sure you call insertHeadStylesSentinel before calling this function.`,
        );
    }
    return sentinel;
}
